import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const StripeConnectSettings: React.FC = () => {
  const { token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState<any>(null);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/stripe/connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsConnected(response.data.connected && response.data.status === 'active');
      setStatus(response.data);
    } catch (error) {
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.post(
        `${API_URL}/stripe/connect/onboard`,
        {
          business_name: 'My Business',
          email: 'business@example.com',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe onboarding
      window.location.href = response.data.onboardingUrl;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to connect Stripe');
      setConnecting(false);
    }
  };

  const handleViewDashboard = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/stripe/connect/dashboard`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.open(response.data.url, '_blank');
    } catch (error: any) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Stripe? You will not be able to accept payments.')) {
      return;
    }

    try {
      await axios.post(
        `${API_URL}/stripe/connect/disconnect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsConnected(false);
      setStatus(null);
      toast.success('Stripe disconnected');
    } catch (error: any) {
      toast.error('Failed to disconnect Stripe');
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <h1>💳 Stripe Payment Settings</h1>
        <p>Accept payments from your customers</p>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {isConnected ? (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#10b981',
                color: 'white',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <span style={{ fontSize: '24px' }}>✅</span>
                <div>
                  <div style={{ fontWeight: '600' }}>Stripe Connected</div>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                    You can now accept payments
                  </div>
                </div>
              </div>

              <h3 style={{ marginBottom: '16px' }}>Account Status:</h3>
              <ul style={{ lineHeight: '2', marginBottom: '24px' }}>
                <li>✅ Charges: {status?.charges_enabled ? 'Enabled' : 'Pending'}</li>
                <li>✅ Payouts: {status?.payouts_enabled ? 'Enabled' : 'Pending'}</li>
                <li>✅ Details: {status?.details_submitted ? 'Complete' : 'Incomplete'}</li>
              </ul>

              <div style={{ 
                padding: '16px', 
                backgroundColor: '#eff6ff', 
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <strong>💰 How Platform Fees Work:</strong>
                <div style={{ fontSize: '14px', marginTop: '8px', color: '#666' }}>
                  When a customer pays you £100:
                  <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
                    <li>You receive: £95</li>
                    <li>ShahoSuite platform fee: £5 (5%)</li>
                  </ul>
                  This fee helps us maintain and improve the platform.
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleViewDashboard}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  📊 View Stripe Dashboard
                </button>

                <button
                  onClick={handleDisconnect}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Disconnect Stripe
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <span style={{ fontSize: '24px' }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: '600' }}>Not Connected</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Connect Stripe to start accepting payments
                  </div>
                </div>
              </div>

              <h3 style={{ marginBottom: '16px' }}>Why Connect Stripe?</h3>
              <ul style={{ lineHeight: '2', marginBottom: '24px' }}>
                <li>💳 Accept card payments from customers</li>
                <li>🔒 Secure payment processing</li>
                <li>📱 Apple Pay & Google Pay support</li>
                <li>💰 Direct deposits to your bank account</li>
                <li>📊 Real-time payment analytics</li>
                <li>🌍 Accept international payments</li>
              </ul>

              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <strong>📝 What You'll Need:</strong>
                <ul style={{ marginTop: '8px', fontSize: '14px' }}>
                  <li>Business name and address</li>
                  <li>Bank account details (for payouts)</li>
                  <li>Business registration number (if applicable)</li>
                  <li>Personal ID verification</li>
                </ul>
              </div>

              <button
                onClick={handleConnect}
                disabled={connecting}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: connecting ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  opacity: connecting ? 0.6 : 1,
                  width: '100%',
                  fontSize: '16px'
                }}
              >
                {connecting ? '⏳ Redirecting to Stripe...' : '🔗 Connect Stripe Account'}
              </button>

              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#e0f2fe', 
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                <strong>ℹ️ About Platform Fees:</strong>
                <div style={{ marginTop: '4px' }}>
                  ShahoSuite charges a 5% platform fee on all transactions to maintain
                  and improve our services. You'll see the exact breakdown on each payment.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeConnectSettings;
